/// A Cody license, read and checked offline (docs/features/licenses.md). The one JavaScript description of the
/// format: the web client verifies with it, the website's page (services/www/public/license.js) builds licenses
/// with it, and the www Worker (services/www/src/license/) checks what it is shown with it. The Swift twin is
/// clients/visionos/Shared/License.swift; both are held to tools/tests/license-vectors.json.
///
/// Nothing here but WebCrypto: RSASSA-PSS (SHA-384, 48-byte salt) for a license, Ed25519 for the revocation list.
///
/// What a license means comes ONLY from which built-in key signed it. The bytes inside name a random id and the
/// owner's key; the `product` written in them is a label for people and is never trusted. Who the owner is plays
/// no part in whether a license is valid: `verify` reads the owner's kind and hands it on, nothing more. Only the
/// site's owner actions (revoke, re-issue — license-owner.js) look at it.
///
///   message   = u8 1 · field(product) · field(id, 32 bytes) · field(owner kind) · field(credentialId) · field(alg, i32) · field(publicKey, SPKI) · field(macKey, SPKI)
///               owner kind "derived": no credential id; an Ed25519 key, its public half alone — what the Mac app makes
///                                     (a random key in its Keychain, one per license) and what the website's page
///                                     derives from a passkey's PRF (license-owner.js). A reader cannot tell which.
///               owner kind "passkey": a passkey's credential id and public key — the website's own page only
///               macKey: the ONE Mac this license is for — a P-256 public key (SPKI DER, 91 bytes) whose private half
///                       is in that Mac's Secure Enclave. The site's signature covers it; license-holder.js is how a
///                       Mac proves it holds it. Every license has one: a message without the field is no license.
///   envelope  = u8 1 · field(keyId) · field(prefix, 32 bytes) · field(message) · field(signature)
///   field(x)  = u16 length (big-endian) · x                    — fixed order, nothing after the last field
///   signed    = prefix · message                               — RFC 9474's "prepared" message
///   text      = "CODY-LICENSE-1 " · base64url(envelope) [ "." · base64url(order) ]   — white space after the header is ignored
///
/// A refundable license travels with a conversion order (the part after the "."): the blinded request for its
/// private replacement, the secret that unblinds the answer, and the owner's passkey approval. Nothing in the
/// order is read when a license is checked.
///   order     = u8 1 · field(keyId) · field(notBefore, u64) · field(blinded) · field(inverse) · field(prepared)
///               · field(authenticatorData) · field(clientDataJSON) · field(signature) · field(flags, 1 byte)
///   approved  = SHA-256( u8 1 · field("convert") · field(id) · field(SHA-256(blinded)) · field(notBefore) · field(keyId) )
///               Who approved it follows from the refundable license's owner kind, as every owner's proof does:
///               "derived" — authenticatorData and clientDataJSON are EMPTY and signature is the owner key's
///                           Ed25519 signature (64 bytes) over "CODY-LICENSE-OWNER-1\n" · approved (license-owner.js)
///               "passkey" — the three fields are the passkey's WebAuthn assertion, its challenge = approved
import { LICENSE_KEYS } from './license-keys.js'

export const HEADER = 'CODY-LICENSE-1'
export const LIST_HEADER = 'CODY-LICENSE-REVOKED-1\n'
export const CHALLENGE_LABEL = 'cody-license-challenge-v1'
const text = new TextEncoder(), words = new TextDecoder('utf-8', { fatal: true })

export class LicenseError extends Error {
  constructor(code, message) { super(message || code); this.code = code }
}
const bad = (why) => new LicenseError('malformed', why)

// MARK: bytes

export function b64u(bytes) {
  let s = ''
  for (const b of new Uint8Array(bytes)) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
export function unb64u(value) {
  const s = String(value ?? '')
  if (!/^[A-Za-z0-9_-]*$/.test(s) || s.length % 4 === 1) throw bad('not base64url')
  const raw = atob(s.replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}
export function join(...parts) {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0))
  let at = 0
  for (const p of parts) { out.set(p, at); at += p.length }
  return out
}
export const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i])
export function field(bytes) {
  if (bytes.length > 0xffff) throw bad('a field is too long')
  return join(Uint8Array.of(bytes.length >> 8, bytes.length & 0xff), bytes)
}
/// `count` fields after a version byte, and nothing else.
export function fields(bytes, version, count, what) {
  if (!(bytes instanceof Uint8Array) || bytes.length < 1 || bytes[0] !== version) throw bad(what + ': unknown version')
  const out = []
  let at = 1
  for (let i = 0; i < count; i++) {
    if (at + 2 > bytes.length) throw bad(what + ': cut short')
    const length = (bytes[at] << 8) | bytes[at + 1]
    at += 2
    if (at + length > bytes.length) throw bad(what + ': cut short')
    out.push(bytes.slice(at, at + length))
    at += length
  }
  if (at !== bytes.length) throw bad(what + ': bytes after the last field')
  return out
}

// MARK: the message and the envelope

/// The kinds of owner the site can check an approval from. A reader takes any kind: it is never part of validity.
export const OWNER_KINDS = ['passkey', 'derived']
const KIND = /^[a-z0-9-]{1,32}$/
/// A passkey names its credential; a derived key must not (that id would tie it back to the passkey).
const ownerShape = (kind, credentialId) => (kind === 'passkey' ? credentialId.length > 0 : kind === 'derived' ? credentialId.length === 0 : true)
/// A P-256 public key as SubjectPublicKeyInfo: 26 fixed bytes, then the uncompressed point (0x04 · X · Y) — 91 in
/// all. The same bytes a Mac's identity key is pinned as (pairing.js); CryptoKit's `derRepresentation`.
export const P256_SPKI = Uint8Array.of(0x30, 0x59, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, 0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, 0x03, 0x42, 0x00)
export const isP256 = (key) => key instanceof Uint8Array && key.length === 91 && key[26] === 0x04 && P256_SPKI.every((b, i) => key[i] === b)

/// {product, id (32 bytes), owner: {kind ('passkey' when left out), credentialId, alg (COSE, e.g. -7), publicKey (SPKI DER)},
/// macKey (P-256, SPKI DER)} → bytes.
export function encodeMessage({ product, id, owner, macKey }) {
  if (!/^[a-z0-9.-]{1,64}$/.test(product || '')) throw bad('product')
  if (!(id instanceof Uint8Array) || id.length !== 32) throw bad('the id is 32 bytes')
  const kind = owner?.kind ?? 'passkey', credentialId = owner?.credentialId ?? new Uint8Array(0)
  if (!KIND.test(kind)) throw bad('owner kind')
  if (credentialId.length > 1023 || !ownerShape(kind, credentialId)) throw bad('credential id')
  if (!Number.isInteger(owner.alg) || owner.alg < -(2 ** 31) || owner.alg >= 2 ** 31) throw bad('alg')
  if (!owner.publicKey?.length || owner.publicKey.length > 2048) throw bad('public key')
  if (!isP256(macKey)) throw bad('the Mac key is a P-256 public key')
  const alg = new Uint8Array(4)
  new DataView(alg.buffer).setInt32(0, owner.alg)
  return join(Uint8Array.of(1), field(text.encode(product)), field(id), field(text.encode(kind)), field(credentialId), field(alg), field(owner.publicKey), field(macKey))
}
export function decodeMessage(bytes) {
  const [product, id, ownerKind, credentialId, alg, publicKey, macKey] = fields(bytes, 1, 7, 'message')
  if (id.length !== 32 || alg.length !== 4 || !publicKey.length) throw bad('message: a field has the wrong size')
  if (!isP256(macKey)) throw bad('message: the Mac key is not a P-256 public key')
  let name, kind
  try { name = words.decode(product); kind = words.decode(ownerKind) } catch { throw bad('message: text') }
  if (!KIND.test(kind) || !ownerShape(kind, credentialId)) throw bad('message: owner')
  return { v: 1, product: name, id, owner: { kind, credentialId, alg: new DataView(alg.buffer).getInt32(0), publicKey }, macKey }
}
export function encodeEnvelope({ keyId, prefix, message, signature }) {
  if (!(prefix instanceof Uint8Array) || prefix.length !== 32) throw bad('the prefix is 32 bytes')
  return join(Uint8Array.of(1), field(text.encode(keyId)), field(prefix), field(message), field(signature))
}
export function decodeEnvelope(bytes) {
  const [keyId, prefix, message, signature] = fields(bytes, 1, 4, 'license')
  if (prefix.length !== 32) throw bad('license: prefix')
  let id
  try { id = words.decode(keyId) } catch { throw bad('license: key id') }
  return { keyId: id, prefix, message, signature }
}

/// The key a person copies (one line), or the file they keep (`wrap`: 64 characters a line).
export function toText(envelope, { wrap = false, order = null } = {}) {
  const body = b64u(envelope instanceof Uint8Array ? envelope : encodeEnvelope(envelope)) + (order ? '.' + b64u(order) : '')
  return wrap ? HEADER + '\n' + body.match(/.{1,64}/g).join('\n') + '\n' : HEADER + ' ' + body
}
/// → the envelope, with `order` (bytes, undecoded) when the text carries one.
export function fromText(value) {
  const s = String(value ?? '').trim()
  if (!s.startsWith(HEADER) || !/^\s/.test(s.slice(HEADER.length, HEADER.length + 1))) throw bad('not a Cody license')
  const parts = s.slice(HEADER.length).replace(/\s+/g, '').split('.')
  if (parts.length > 2 || !parts[0]) throw bad('not a Cody license')
  const envelope = decodeEnvelope(unb64u(parts[0]))
  return parts[1] ? { ...envelope, order: unb64u(parts[1]) } : envelope
}
/// The same text without its order: the license alone.
export function bare(value) { return toText(encodeEnvelope(fromText(value))) }

// MARK: the conversion order (a refundable license → its private replacement)

export const u64 = (n) => { const b = new Uint8Array(8); new DataView(b.buffer).setBigUint64(0, BigInt(n)); return b }
export const AUTO = 1   // flags: submit by itself once notBefore has passed ("Keep it refundable" clears it)
/// `signature` (64 bytes): an Ed25519 owner's approval — in place of `assertion`, a passkey's.
export function encodeOrder({ keyId, notBefore, blinded, inverse, prepared, assertion, signature, flags = AUTO }) {
  if (signature) {
    if (assertion || !(signature instanceof Uint8Array) || signature.length !== 64) throw bad('order: an Ed25519 approval is 64 bytes, alone')
    assertion = { authenticatorData: new Uint8Array(0), clientDataJSON: new Uint8Array(0), signature }
  }
  return join(Uint8Array.of(1), field(text.encode(keyId)), field(u64(notBefore)), field(blinded), field(inverse), field(prepared),
    field(assertion.authenticatorData), field(assertion.clientDataJSON), field(assertion.signature), field(Uint8Array.of(flags)))
}
export function decodeOrder(bytes) {
  const [keyId, notBefore, blinded, inverse, prepared, authenticatorData, clientDataJSON, signature, flags] = fields(bytes, 1, 9, 'order')
  if (notBefore.length !== 8 || flags.length !== 1 || prepared.length <= 32 || !blinded.length || blinded.length !== inverse.length) throw bad('order: a field has the wrong size')
  let id
  try { id = words.decode(keyId) } catch { throw bad('order: key id') }
  // an Ed25519 owner's approval is the signature alone; a passkey's is all three fields
  const plain = !authenticatorData.length && !clientDataJSON.length
  if (plain ? signature.length !== 64 : !authenticatorData.length || !clientDataJSON.length || !signature.length) throw bad('order: the approval is incomplete')
  return { keyId: id, notBefore: Number(new DataView(notBefore.buffer).getBigUint64(0)), blinded, inverse, prepared, ...(plain ? { signature } : { assertion: { authenticatorData, clientDataJSON, signature } }), flags: flags[0] }
}
/// What the owner approves a conversion with — a passkey's challenge, or what an Ed25519 owner key signs behind
/// its header (license-owner.js `ownerActionBytes`). No nonce from the site: the order can only ever do one
/// thing, once.
export async function orderChallenge({ id, blinded, notBefore, keyId }) {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', blinded))
  const bytes = join(Uint8Array.of(1), field(text.encode('convert')), field(id), field(digest), field(u64(notBefore)), field(text.encode(keyId)))
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
}

// MARK: checking

const imported = new Map()
function verifyKey(key) {
  if (!imported.has(key.spki)) imported.set(key.spki, crypto.subtle.importKey('spki', unb64u(key.spki), { name: 'RSA-PSS', hash: 'SHA-384' }, false, ['verify']))
  return imported.get(key.spki)
}

/// Is this license good, offline? → {keyId, grants, mode, refundable, id, idText, owner, macKey}; throws a LicenseError
/// whose `code` is malformed · unknown-key · distrusted · bad-signature · wrong-mode · revoked.
///   keys     the built-in list (license-keys.js), or what `trustedKeys` made of it and the site's list (license-keylist.js)
///   modes    which keys count: a release build takes 'live' only
///   revoked  a list `verifyList` returned (or nothing: no list seen yet) — the site-signed list of customer
///            license ids. The owner-signed list of role licenses (license-rolelist.js) says nothing about a
///            customer's license and is ignored here.
export async function verify(value, { keys = LICENSE_KEYS.keys, modes = ['live'], revoked = null } = {}) {
  const envelope = typeof value === 'string' ? fromText(value) : value
  const key = keys.find((k) => k.id === envelope.keyId)
  if (!key) throw new LicenseError('unknown-key', 'This license was not signed by a key this app knows.')
  // a key an owner distrusted (it leaked): every license under it fails, whenever it was made — a blind license has no date to go by
  if (key.distrusted) throw new LicenseError('distrusted', 'This license’s signing key is no longer trusted; claim a new license on the website.')
  const good = await crypto.subtle.verify({ name: 'RSA-PSS', saltLength: 48 }, await verifyKey(key), envelope.signature, join(envelope.prefix, envelope.message))
  if (!good) throw new LicenseError('bad-signature', 'This license’s signature does not check out.')
  const message = decodeMessage(envelope.message)   // after the signature: nothing unsigned is ever read
  if (!modes.includes(key.mode)) throw new LicenseError('wrong-mode', 'This is a ' + key.mode + ' license.')
  const idText = b64u(message.id)
  if (revoked && revoked.kind !== 'roles' && revoked.ids.includes(idText)) throw new LicenseError('revoked', 'This license was revoked by its owner.')
  // what it is comes from the key alone — message.product is never looked at
  // …whether the Mac showing it is the Mac it is for is another question: license-holder.js `verifyPresented`
  return { keyId: key.id, grants: key.grants, mode: key.mode, refundable: key.product !== key.grants, id: message.id, idText, owner: message.owner, macKey: message.macKey }
}

// MARK: the revocation list (/.well-known/cody-license-revoked.json) — customer license ids, signed by the site
// (conversions and owners' revokes add to it with nobody at a hardware key). Role licenses have their own list,
// which only an owner can sign: license-rolelist.js. A role license's id on THIS list means nothing.

/// What the list key signs: the header, the version and the issue time (u64 each), the count (u32), then the
/// ids, 32 bytes each, in ascending order.
export function listBytes({ version, issued, ids }) {
  const head = new Uint8Array(20)
  const view = new DataView(head.buffer)
  view.setBigUint64(0, BigInt(version)); view.setBigUint64(8, BigInt(issued)); view.setUint32(16, ids.length)
  return join(text.encode(LIST_HEADER), head, ...ids.map((id) => (typeof id === 'string' ? unb64u(id) : id)))
}
export function sortedIds(ids) {
  const hex = (id) => Array.from(unb64u(id), (b) => b.toString(16).padStart(2, '0')).join('')
  return [...new Set(ids)].sort((a, b) => (hex(a) < hex(b) ? -1 : 1))
}

/// Which mode a list key is: its `mode` where the key list says one (tools/license-keygen.mjs writes it), else the
/// second part of its id — a list key's id is `revoked.<mode>.<epoch>`. The two always agree.
export const listKeyMode = (key) => key.mode ?? String(key.id).split('.')[1]

/// A list as fetched (an object or its JSON text) → {kind: 'licenses', version, issued, ids, key} once its signature checks out
/// against a built-in list key whose mode this reader takes; throws LicenseError
/// malformed · unknown-key · bad-signature · wrong-mode · rollback · unsupported — checked in that order.
///   modes    which list keys count — the rule licenses go by (`verify`): a release build takes 'live' only, so a
///            list signed with a TEST list key (staging's, kept with less care than a live one) can neither
///            revoke nor un-revoke anything there
///   atLeast  the version of the list already cached: an older one is refused, so nobody can un-revoke by replay
export async function verifyList(value, { keys = LICENSE_KEYS.lists, modes = ['live'], atLeast = 0 } = {}) {
  let list
  try { list = typeof value === 'string' ? JSON.parse(value) : value } catch { throw bad('list: not JSON') }
  if (list?.v !== 1 || !Number.isSafeInteger(list.version) || list.version < 0 || !Number.isSafeInteger(list.issued) || list.issued < 0 || !Array.isArray(list.ids)) throw bad('list: shape')
  const ids = list.ids.map((id) => { const b = unb64u(id); if (b.length !== 32 || b64u(b) !== id) throw bad('list: an id is not 32 bytes'); return id })
  if (JSON.stringify(sortedIds(ids)) !== JSON.stringify(ids)) throw bad('list: ids out of order')
  const key = keys.find((k) => k.id === list.key)
  if (!key) throw new LicenseError('unknown-key', 'The revocation list was not signed by a key this app knows.')
  let good
  try {
    const imported = await crypto.subtle.importKey('raw', unb64u(key.key), { name: 'Ed25519' }, false, ['verify'])
    good = await crypto.subtle.verify({ name: 'Ed25519' }, imported, unb64u(list.signature), listBytes({ version: list.version, issued: list.issued, ids }))
  } catch (e) {
    if (e instanceof LicenseError) throw e
    throw new LicenseError('unsupported', 'This browser cannot check the revocation list (no Ed25519).')
  }
  if (!good) throw new LicenseError('bad-signature', 'The revocation list’s signature does not check out.')
  if (!modes.includes(listKeyMode(key))) throw new LicenseError('wrong-mode', 'This is a ' + listKeyMode(key) + ' revocation list.')
  if (list.version < atLeast) throw new LicenseError('rollback', 'This revocation list is older than the one already seen.')
  return { kind: 'licenses', version: list.version, issued: list.issued, ids, key: key.id }
}

// MARK: the owner's passkey

/// The relying party a license passkey belongs to: the one domain every name of the site is under, or the host
/// itself anywhere else (a local worker, a tunnel).
export function rpIdFor(hostname) {
  return hostname === 'codymccodeface.com' || hostname.endsWith('.codymccodeface.com') ? 'codymccodeface.com' : hostname
}

/// What the passkey is asked to sign for an owner's action: the site's one-use nonce, bound to the action and its
/// subject (a license id, a vault handle) by hashing — so the site's nonce row never has to name either.
export async function challengeFor(nonce, action, subject) {
  const digest = await crypto.subtle.digest('SHA-256', text.encode([CHALLENGE_LABEL, nonce, action, subject].join('\n')))
  return new Uint8Array(digest)
}
